import React, { useState } from 'react';
import { usePayrollProfiles, useEmployees, useSalaryComponents, useCreatePayrollProfile, useUpdatePayrollProfile } from '@/api';
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Badge, Modal } from '@/components/ui';
import { LoadingSpinner, Pagination } from '@/components/common';
import { Plus, Edit2, DollarSign } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { extractPaginatedData } from '@/utils/pagination';
import { useDebouncedValue } from '@/utils/useDebouncedValue';

export default function PayrollProfilesPage() {
  const perPage = 10;
  const { can } = useAuth();
  const canManage = can('manage_payroll');
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingProfile, setEditingProfile] = useState<any>(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim());

  const { data: profilesData, isLoading } = usePayrollProfiles({ page, per_page: perPage });
  const { data: employeesData } = useEmployees({ per_page: 100, search: debouncedSearch, is_active: true });
  const { data: componentsData } = useSalaryComponents({ per_page: 200 });

  const createMutation = useCreatePayrollProfile();
  const updateMutation = useUpdatePayrollProfile();

  const { items: profiles, meta } = extractPaginatedData<any>(profilesData, page, perPage);
  const { items: employees } = extractPaginatedData<any>(employeesData, 1, 100);
  const { items: components } = extractPaginatedData<any>(componentsData, 1, 200);

  // Group employees with and without profiles
  const employeeProfileMap = new Map(profiles.map((p: any) => [p.employee_id, p]));

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6 page-shell">
      <div className="page-toolbar">
        <h1 className="text-3xl font-bold">Профили зарплаты</h1>
        {canManage && (
          <Button onClick={() => { setShowForm(!showForm); setEditingProfile(null); }}>
            <Plus className="w-4 h-4 mr-2" />
            Добавить профиль
          </Button>
        )}
      </div>

      <Modal
        isOpen={showForm || !!editingProfile}
        onClose={() => { setShowForm(false); setEditingProfile(null); }}
        title={editingProfile ? 'Редактировать профиль' : 'Новый профиль зарплаты'}
      >
        <PayrollProfileForm
          employees={employees}
          components={components}
          initialData={editingProfile}
          onSubmit={async (data) => {
            if (editingProfile) {
              await updateMutation.mutateAsync({ id: editingProfile.id, data });
            } else {
              await createMutation.mutateAsync(data);
            }
            setShowForm(false);
            setEditingProfile(null);
          }}
          onCancel={() => { setShowForm(false); setEditingProfile(null); }}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      </Modal>

      {/* Search */}
      <div>
        <Input
          placeholder="Поиск сотрудников..."
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
        />
      </div>

      {/* Profiles Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto ui-table-wrap">
            <table className="w-full ui-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold">Сотрудник</th>
                  <th className="text-left py-3 px-4 font-semibold">Отдел</th>
                  <th className="text-right py-3 px-4 font-semibold">Базовая зарплата</th>
                  <th className="text-left py-3 px-4 font-semibold">Тип оплаты</th>
                  <th className="text-left py-3 px-4 font-semibold">Коэфф. перераб.</th>
                  <th className="text-left py-3 px-4 font-semibold">Статус</th>
                  <th className="text-left py-3 px-4 font-semibold">Действия</th>
                </tr>
              </thead>
              <tbody>
                {profiles.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-10 text-gray-500">
                      Профили зарплаты не настроены
                    </td>
                  </tr>
                ) : (
                  profiles.map((profile: any) => (
                    <tr key={profile.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-green-600" />
                          <div>
                            <div className="font-medium">{profile.employee?.full_name}</div>
                            <div className="text-sm text-gray-500">{profile.employee?.employee_id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">{profile.employee?.department?.name || '-'}</td>
                      <td className="py-3 px-4 text-right font-mono">
                        {Number(profile.base_salary || 0).toLocaleString('en-US', {
                          style: 'currency',
                          currency: 'UZS',
                          maximumFractionDigits: 0,
                        })}
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="outline">{profile.payroll_type || 'monthly'}</Badge>
                      </td>
                      <td className="py-3 px-4 text-sm font-mono">
                        {profile.overtime_multiplier ? `x${profile.overtime_multiplier}` : 'x1.5'}
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant={profile.is_active ? 'default' : 'secondary'}>
                          {profile.is_active ? 'Активен' : 'Неактивен'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        {canManage && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingProfile(profile)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      <Pagination meta={meta} onPageChange={setPage} />
    </div>
  );
}

interface PayrollProfileFormProps {
  employees: any[];
  components: any[];
  initialData?: any;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
}

function PayrollProfileForm({ employees, components, initialData, onSubmit, onCancel, isLoading }: PayrollProfileFormProps) {
  const [employeeId, setEmployeeId] = useState(initialData?.employee_id?.toString() || '');
  const [baseSalary, setBaseSalary] = useState(initialData?.base_salary?.toString() || '');
  const [payType, setPayType] = useState(initialData?.payroll_type || 'monthly');
  const [overtimeMultiplier, setOvertimeMultiplier] = useState(initialData?.overtime_multiplier?.toString() || '1.5');
  const [isActive, setIsActive] = useState(initialData?.is_active ?? true);
  const [effectiveFrom, setEffectiveFrom] = useState(
    initialData?.effective_from ? String(initialData.effective_from).slice(0, 10) : new Date().toISOString().slice(0, 10)
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const basePayload: any = {
      employee_id: Number(employeeId),
      payroll_type: payType,
      effective_from: effectiveFrom,
      is_active: isActive,
      overtime_multiplier: Number(overtimeMultiplier || 1.5),
    };

    if (payType === 'monthly') {
      basePayload.base_salary = Number(baseSalary || 0);
    }
    if (payType === 'hourly') {
      basePayload.hourly_rate = Number(baseSalary || 0);
    }
    if (payType === 'daily') {
      basePayload.daily_rate = Number(baseSalary || 0);
    }

    onSubmit({
      ...basePayload,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium mb-1">Сотрудник *</label>
          <select
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            required
            disabled={!!initialData}
            className="w-full border rounded-md px-3 py-2 disabled:bg-gray-100"
          >
            <option value="">Выберите сотрудника</option>
            {employees.map((emp: any) => (
              <option key={emp.id} value={emp.id}>
                {emp.full_name} ({emp.employee_id})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Базовая зарплата *</label>
          <Input
            type="number"
            placeholder="например, 5000000"
            value={baseSalary}
            onChange={(e) => setBaseSalary(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Тип оплаты</label>
          <select
            value={payType}
            onChange={(e) => setPayType(e.target.value)}
            className="w-full border rounded-md px-3 py-2"
          >
            <option value="monthly">Ежемесячно</option>
            <option value="hourly">Почасово</option>
            <option value="daily">Ежедневно</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Коэфф. переработки</label>
          <Input
            type="number"
            step="0.1"
            min="1"
            max="3"
            placeholder="например, 1.5"
            value={overtimeMultiplier}
            onChange={(e) => setOvertimeMultiplier(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Действует с *</label>
          <Input
            type="date"
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
            required
          />
        </div>
        <div className="sm:col-span-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm">Активен</span>
          </label>
        </div>
      </div>
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel}>
          Отмена
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Сохранение...' : initialData ? 'Обновить' : 'Создать'}
        </Button>
      </div>
    </form>
  );
}


